import { FC, useState } from 'react';
import { Modal, Typography } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import { shopItemCategories } from 'types/MarketplaceItems';
import { APP } from 'config';
import { formatCountry } from 'utils/format';
import { Stack } from '@mui/material';
import {
  RecognitionRewardsConfig,
  useRecognitionRewards,
  useRemoveRecognitionRewardsConfig,
  useUpdateRecognitionRewardsConfig,
} from 'store/recognition-rewards';
import {
  FilterButton,
  Filters,
  SuppliersFilter,
  SelectFilter,
} from 'components/filters';
import { isEqual } from 'lodash';
import { useMe } from 'state/Administrators';
import { taxExemptionStatuses } from 'store/shop';

type Values = RecognitionRewardsConfig;

const INITIAL_VALUES: Values = {
  categories: [],
  countries: [],
  cities: [],
  taxExemptionStatus: [],
  taxExemptionCountries: [],
  suppliers: {
    value: [],
    isInverted: false,
  },
};

interface Props extends ModalProps {
  config: Values;
}

export const RewardsConfigModal: FC<Props> = ({ closeModal, config }) => {
  const { t, i18n } = useTranslation();
  const { taxExemptionCountries } = useMe();
  const predefinedToasts = usePredefinedToasts();

  const [filters, setFilters] = useState<Values>({
    ...config,
    taxExemptionCountries,
  });

  const { data: rewards, isLoading } = useRecognitionRewards(filters);

  const { mutate: updateConfig, isLoading: isUpdating } =
    useUpdateRecognitionRewardsConfig();
  const { mutate: removeConfig, isLoading: isRemoving } =
    useRemoveRecognitionRewardsConfig();

  return (
    <Modal.Content
      title={t('recognitions.rewards.actions.set_config')}
      actions={[
        {
          variant: 'danger-outline',
          onClick: () => {
            removeConfig(undefined, {
              onSuccess: () => {
                closeModal({ action: 'CLOSE' });
                predefinedToasts.success.updated();
              },
            });
          },
          label: t('recognitions.rewards.actions.remove_config'),
          disabled: isRemoving,
        },
        {
          variant: 'neutral-outline',
          onClick: () => closeModal({ action: 'CLOSE' }),
          label: t('common.cancel'),
        },
        ...(isEqual(config, filters)
          ? []
          : [
              {
                variant: 'primary',
                onClick: () => {
                  updateConfig(
                    {
                      config: {
                        suppliers: filters.suppliers,
                        categories: filters.categories,
                        countries: filters.countries,
                        cities: filters.cities,
                        taxExemptionCountries,
                        taxExemptionStatus: filters.taxExemptionStatus,
                      },
                    },
                    {
                      onSuccess: () => {
                        closeModal({ action: 'CLOSE' });
                        predefinedToasts.success.updated();
                      },
                    },
                  );
                },
                label: t('marketplace.confirmSettings', {
                  count: rewards?.total,
                }),
                disabled: isLoading || isUpdating,
              } as const,
            ]),
      ]}
    >
      <Stack gap={2}>
        <Typography variant="p1" color="textSecondary">
          {t('recognitions.rewards.config.description')}
        </Typography>

        <Filters
          value={filters}
          initialValues={INITIAL_VALUES}
          onChange={setFilters}
        >
          {({ value, initialValues, setFilter }) => (
            <>
              <FilterButton
                label={t('marketplaceItems.supplier')}
                value={value.suppliers}
                initialValue={initialValues.suppliers}
                onChange={(v) => setFilter('suppliers', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SuppliersFilter
                    value={value}
                    onApplyFilter={applyFilter}
                    onClearFilter={clearFilter}
                    onCancel={close}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('benefits.benefit_type')}
                value={value.categories}
                initialValue={initialValues.categories}
                onChange={(v) => setFilter('categories', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    name="categories"
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    multiple
                    onClearFilter={clearFilter}
                    onCancel={close}
                    options={shopItemCategories.map((category) => ({
                      key: category,
                      label: t(`marketplaceItems.itemCategory-${category}`),
                    }))}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('benefits.country')}
                value={value.countries}
                initialValue={initialValues.countries}
                onChange={(v) => setFilter('countries', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    name="countries"
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                    multiple
                    options={APP.countries.alpha3.map((alpha3) => ({
                      key: alpha3,
                      label: formatCountry(i18n.language, alpha3),
                    }))}
                  />
                )}
              </FilterButton>

              <FilterButton
                label={t('common.city.label')}
                value={value.cities}
                initialValue={initialValues.cities}
                onChange={(v) => setFilter('cities', v)}
              >
                {({ value, applyFilter, clearFilter, close }) => (
                  <SelectFilter
                    name="cities"
                    value={{ value }}
                    onApplyFilter={({ value }) => applyFilter(value)}
                    onClearFilter={clearFilter}
                    onCancel={close}
                    multiple
                    options={APP.cities.map((city) => ({
                      key: city,
                      label: city,
                    }))}
                  />
                )}
              </FilterButton>

              {taxExemptionCountries.map((country) => (
                <FilterButton
                  label={t('taxExemptions.country.label', {
                    country: formatCountry(i18n.language, country),
                  })}
                  value={value.taxExemptionStatus}
                  initialValue={initialValues.taxExemptionStatus}
                  onChange={(v) => setFilter('taxExemptionStatus', v)}
                  key={country}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SelectFilter
                      name="taxExemptionStatus"
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      multiple
                      searchable={false}
                      options={taxExemptionStatuses.map((status) => ({
                        key: status,
                        label: t(`taxExemptions.status.${status}`),
                      }))}
                    />
                  )}
                </FilterButton>
              ))}
            </>
          )}
        </Filters>
      </Stack>
    </Modal.Content>
  );
};
